import { computed, ref } from 'vue';

import { InactiveAccounts } from "~/types/equipment"

/**
 * Hook to manage table sorting for inactive accounts
 *
 * @param accounts - Function that returns the array of accounts to sort
 * @returns Object with sort state, sorted accounts and sort function
 *
 * @example
 * ```typescript
 * const { sortState, sortedAccounts, handleSort } = useTableSorting(() => accounts.value?.data);
 * ```
 */
export default function useTableSorting(accounts: () => InactiveAccounts.AccountWithoutMovement[] | undefined) {
  const sortState = ref({ column: '', direction: '' });

  const sortedAccounts = computed(() => {
    const data = accounts();
    if (!data) return [];

    const { column, direction } = sortState.value;
    if (!column) return data;

    const isAscending = direction === 'asc';

    const getColumnValue = (item: InactiveAccounts.AccountWithoutMovement) => {
      if (column === 'partner_id') return item[column] ?? 0;
      if (column === 'inserted_at' || column === 'updated_at') {
        return new Date(item[column] || 0).getTime();
      }
      return item[column as keyof InactiveAccounts.AccountWithoutMovement]?.toString().toLowerCase() || '';
    };

    return [...data].sort((firstItem, secondItem) => {
      const firstValue = getColumnValue(firstItem);
      const secondValue = getColumnValue(secondItem);

      if (typeof firstValue === 'number' && typeof secondValue === 'number') {
        return isAscending ? firstValue - secondValue : secondValue - firstValue;
      }

      const firstStr = String(firstValue);
      const secondStr = String(secondValue);

      return isAscending
        ? firstStr.localeCompare(secondStr)
        : secondStr.localeCompare(firstStr);
    });
  });

  function handleSort(column: string) {
    if (sortState.value.column === column) {
      sortState.value.direction = sortState.value.direction === 'asc' ? 'desc' : 'asc';
    } else {
      sortState.value.column = column;
      sortState.value.direction = 'asc';
    }
  }

  return {
    handleSort,
    sortedAccounts,
    sortState,
  };
}
